import React from "react";
import './TermsOfUse.css';

const TermsOfUse = () => {
    return (
        <div className="terms-container">
            <div className="terms-content">
                <div className="terms-header">
                    <h1>Terms of Use</h1>
                    <p className="last-updated">Last Updated: March 6, 2025</p>
                </div>

                <div className="terms-body">
                    <p className="intro">
                        Welcome to ClientBloom.ai. The services on our site are provided by Client Bloom LLC, a limited liability company formed in Wyoming. These Terms of Use govern your use of our platform and services. By accessing or using our services, you agree to be bound by these terms. If you do not agree, do not use our services.
                    </p>

                    <section className="terms-section">
                        <h2>1. Acceptance of Terms</h2>
                        <p>By using the Client Bloom platform, website, or related services, you acknowledge that you have read, understood, and agreed to these Terms of Use. Your continued use of our services constitutes your acceptance of these terms in their entirety. If you do not agree, you should discontinue use immediately.</p>
                    </section>

                    <section className="terms-section">
                        <h2>2. Access and Authorized Use</h2>
                        <p>Your access to and use of our services is conditional upon compliance with these Terms. We grant you a limited, non-exclusive, non-transferable, and revocable license to use our platform in accordance with these Terms.</p>
                    </section>

                    <section className="terms-section">
                        <h2>3. Payments, Subscriptions, and Cancellations</h2>
                        <p>Our platform operates on a subscription-based model, with payments securely processed through Stripe. Subscription fees are charged in advance and will continue on a recurring basis until canceled.</p>
                    </section>

                    <section className="terms-section">
                        <h2>4. Service Availability and Modifications</h2>
                        <p>We strive to maintain 99.9 percent uptime for our services, but we do not guarantee uninterrupted or error-free operation. There may be instances where our platform is temporarily unavailable due to maintenance, technical issues, or circumstances beyond our control.</p>
                    </section>

                    <section className="terms-section">
                        <h2>5. Termination and Account Deletion</h2>
                        <p>We reserve the right to suspend or terminate your account if we determine that you have violated these Terms, engaged in fraudulent or illegal activity, or acted in a manner that disrupts the integrity of our platform.</p>
                    </section>

                    <section className="terms-section">
                        <h2>6. Intellectual Property Rights</h2>
                        <p>All content, software, trademarks, and branding associated with our platform are the exclusive property of Client Bloom LLC or its licensors. You may not copy, modify, distribute, or create derivative works without our express written permission.</p>
                    </section>

                    <section className="terms-section">
                        <h2>7. User Accounts and Security</h2>
                        <p>If you create an account on ClientBloom, you are responsible for maintaining the confidentiality of your account information and password. You agree to accept responsibility for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.</p>
                    </section>

                    <section className="terms-section">
                        <h2>8. Prohibited Activities</h2>
                        <p>You agree not to use ClientBloom to:</p>
                        <ul>
                            <li>Violate any applicable laws or regulations</li>
                            <li>Infringe upon or violate any intellectual property rights</li>
                            <li>Harass, threaten, defame, or abuse any individual or organization</li>
                            <li>Transmit any viruses, malware, or harmful code</li>
                            <li>Attempt to gain unauthorized access to any portion of the Service</li>
                            <li>Interfere with the normal operation of the Service</li>
                        </ul>
                    </section>

                    <section className="terms-section">
                        <h2>9. Zoom Integration</h2>
                        <p>If you use the Zoom integration feature with ClientBloom:</p>
                        <ul>
                            <li>You authorize ClientBloom to access your Zoom account and retrieve recordings and transcriptions</li>
                            <li>You are responsible for maintaining Zoom account security</li>
                            <li>You can disconnect the integration at any time through ClientBloom settings or Zoom Marketplace</li>
                            <li>Historical data will remain in ClientBloom after disconnection unless explicitly deleted</li>
                            <li>ClientBloom complies with Zoom's API terms and data handling requirements</li>
                            <li>Only cloud recordings with transcriptions generated by Zoom will be processed</li>
                            <li>Client matching is performed automatically based on participant email addresses</li>
                        </ul>
                    </section>

                    <section className="terms-section">
                        <h2>10. AI and Machine Learning Disclaimer</h2>
                        <p>If our platform includes AI-powered features, the results generated by AI are for informational purposes only. We do not guarantee the accuracy, completeness, or reliability of AI-generated insights.</p>
                    </section>

                    <section className="terms-section">
                        <h2>11. External Links and Third-Party Services</h2>
                        <p>Our website may contain links to third-party services, including Zoom. We are not responsible for their content, practices, or terms of service. Use of third-party services is at your own risk.</p>
                    </section>

                    <section className="terms-section">
                        <h2>12. Limitation of Liability</h2>
                        <p>To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our platform, including but not limited to loss of data, profits, or business interruption.</p>
                    </section>

                    <section className="terms-section">
                        <h2>13. Indemnification</h2>
                        <p>You agree to indemnify, defend, and hold harmless Client Bloom LLC and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising out of your use of the Service or violation of these Terms of Use.</p>
                    </section>

                    <section className="terms-section">
                        <h2>14. Dispute Resolution and Governing Law</h2>
                        <p>These Terms shall be governed by and construed in accordance with the laws of the State of Wyoming, without regard to conflicts of law principles. Any disputes arising from or related to these Terms shall be resolved through binding arbitration in Wyoming.</p>
                    </section>

                    <section className="terms-section">
                        <h2>15. Modifications to Terms</h2>
                        <p>ClientBloom may revise these terms of service at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service. We encourage you to review these terms periodically.</p>
                    </section>

                    <section className="terms-section">
                        <h2>16. Contact Information</h2>
                        <p>For legal or terms-related inquiries, you may contact us at:</p>
                        <address>
                            <strong>Client Bloom LLC</strong><br />
                            30 N Gould St Ste R<br />
                            Sheridan, WY 82801<br />
                            Email: <a href="mailto:hello@clientbloom.ai">hello@clientbloom.ai</a>
                        </address>
                    </section>

                    <p className="final-note">
                        By using our services, you confirm that you have read, understood, and agreed to these Terms of Use.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TermsOfUse;
